import {DialogsEnum} from "@/lib/enums/DialogsEnum";
import {DialogConfig, DialogLayer, OrchestratorContext} from "../types";

/**
 * Gate for the corner toasts (PWA install, push permission): only surface when
 * nothing else is on screen — neither an orchestrated dialog (engine truth) nor
 * a legacy Radix dialog opened directly by a page (DOM probe). The latter check
 * is the transitional bridge until every dialog is orchestrator-opened.
 */
function isScreenClear(ctx: OrchestratorContext): boolean {
    if (ctx.activeDialogs.length > 0) return false;
    return !(typeof document !== "undefined" &&
        document.querySelector("[data-radix-dialog-content]"));
}

/**
 * Declarative registry for every dialog in the app.
 *
 * This is the ONLY place priorities/modes/limits live. Adding a new dialog =
 * adding one entry here; components never decide their own ordering or gating.
 *
 * Priority is a function of runtime context, so e.g. WALLET outranks marketing
 * while a deposit is pending, and session-critical dialogs outrank everything.
 */
export const DIALOG_CONFIGS: DialogConfig[] = [
    // ── Critical / system — top-most, interrupt everything ───────────────────
    {
        id: DialogsEnum.REGIONAL_RESTRICTIONS,
        layer: DialogLayer.CRITICAL,
        category: "system",
        mode: "replace",
        blocking: true,
        persistent: true,
        priority: () => 1000,
    },
    {
        id: DialogsEnum.CONFIRM,
        layer: DialogLayer.CRITICAL,
        category: "system",
        mode: "stack",          // always on top of whatever triggered it
        blocking: true,
        dedupe: false,          // multiple confirms can be requested in sequence
        priority: () => 900,
    },
    {
        id: DialogsEnum.LOG_OUT,
        layer: DialogLayer.CRITICAL,
        category: "system",
        mode: "replace",
        blocking: true,
        priority: () => 850,
    },
    // AC-830 account soft-delete confirmation (only reachable from /account/delete).
    {
        id: DialogsEnum.DELETE_ACCOUNT,
        layer: DialogLayer.CRITICAL,
        category: "system",
        mode: "replace",
        blocking: true,
        priority: () => 850,
        canShow: (ctx) => ctx.isAuthenticated,
    },

    // ── Auth / security — above marketing & content ──────────────────────────
    {
        id: DialogsEnum.AUTH,
        layer: DialogLayer.AUTH,
        category: "auth",
        mode: "stack",
        blocking: true,
        // higher priority on protected routes so it wins the queue
        priority: (ctx) => (ctx.route.startsWith("/account") ? 800 : 700),
        canShow: (ctx) => !ctx.isAuthenticated,
    },
    {
        id: DialogsEnum.SET_NEW_PASSWORD,
        layer: DialogLayer.AUTH,
        category: "auth",
        mode: "replace",
        blocking: true,
        priority: () => 780,
    },
    {
        id: DialogsEnum.CONTACT_VERIFY,
        layer: DialogLayer.AUTH,
        category: "auth",
        mode: "queue",
        blocking: true,
        requiresAuth: true,
        priority: () => 600,
    },

    // ── Wallet / payments — flow layer ───────────────────────────────────────
    {
        id: DialogsEnum.WALLET,
        layer: DialogLayer.FLOW,
        category: "wallet",
        mode: "stack",
        blocking: true,
        requiresAuth: true,          // demonstrates the AUTH dependency flow
        priority: (ctx) => (ctx.hasPendingDeposit ? 650 : 500),
    },
    {
        id: DialogsEnum.CONTINUE_TOP_UP,
        layer: DialogLayer.FLOW,
        category: "wallet",
        mode: "stack",
        // Requested by WalletDialog only while the cashier is open, so the WALLET
        // prerequisite is satisfied by the caller's context. Re-add
        // `dependencies: [DialogsEnum.WALLET]` once WALLET itself is opened
        // through the orchestrator rather than directly by pages.
        priority: () => 480,
    },
    {
        id: DialogsEnum.FUNDS_CREDITED,
        layer: DialogLayer.FLOW,
        category: "wallet",
        mode: "replace",             // success state supersedes the cashier
        requiresAuth: true,
        priority: () => 520,
    },
    {
        id: DialogsEnum.TX_CANCELLED,
        layer: DialogLayer.FLOW,
        category: "wallet",
        mode: "stack",
        requiresAuth: true,
        priority: () => 510,
    },
    {
        id: DialogsEnum.WAGER_REQUIREMENT,
        layer: DialogLayer.FLOW,
        category: "wallet",
        // Stack over the open WALLET dialog instead of queueing behind it —
        // it's raised from inside the withdraw flow and must surface immediately
        // on top of the cashier, not after it closes.
        mode: "stack",
        requiresAuth: true,
        priority: () => 400,
    },

    // ── Bonuses ──────────────────────────────────────────────────────────────
    {
        id: DialogsEnum.BONUS_CONDITIONS,
        layer: DialogLayer.FLOW,
        category: "bonus",
        mode: "stack",
        requiresAuth: true,
        priority: () => 420,
    },
    {
        id: DialogsEnum.UNLOCK_NEXT_BONUS,
        layer: DialogLayer.FLOW,
        category: "bonus",
        mode: "queue",
        blocking: true,             // one promo at a time (preserves legacy queue)
        requiresAuth: true,
        // Frequency is gated per-session by the home page (session-id storage),
        // so no orchestrator cooldown here — it would double-throttle.
        priority: () => 360,
    },
    {
        // Player-initiated redemption feedback — stacks over whatever is open
        // (it is raised from the bonus centre right after the Apply click).
        id: DialogsEnum.PROMO_CODE_RESULT,
        layer: DialogLayer.FLOW,
        category: "bonus",
        mode: "stack",
        requiresAuth: true,
        priority: () => 430,
    },
    {
        id: DialogsEnum.FREE_SPINS_GAME,
        layer: DialogLayer.FLOW,
        category: "bonus",
        mode: "queue",
        requiresAuth: true,
        priority: () => 340,
    },

    // ── Bonuses (user-triggered) ─────────────────────────────────────────────
    {
        id: DialogsEnum.WELCOME_BONUS,
        layer: DialogLayer.FLOW,
        category: "bonus",
        mode: "stack",              // opened on demand from the hero slide
        priority: () => 300,
    },

    // ── Marketing — low layer, heavily throttled, easily preempted ───────────
    {
        id: DialogsEnum.WELCOME_BONUS_PROMO,
        layer: DialogLayer.MARKETING,
        category: "marketing",
        mode: "queue",
        blocking: true,             // one promo at a time (preserves legacy queue)
        // Shown once per session via the home page's session-id gate.
        priority: () => 180,
    },

    // ── Utility / content ─────────────────────────────────────────────────────
    {
        id: DialogsEnum.SEARCH,
        layer: DialogLayer.CONTENT,
        category: "utility",
        mode: "stack",
        priority: () => 300,
    },

    // ── PWA install — a corner toast, near the bottom of the priority scale ──
    {
        id: DialogsEnum.PWA_INSTALL,
        layer: DialogLayer.MARKETING,
        category: "marketing",   // suppressed during deposit/auth flows
        mode: "queue",
        priority: () => 10,       // never preempts a real dialog
        canShow: isScreenClear,
    },

    // ── Push-permission soft prompt — a corner toast, below even PWA ─────────
    // A modal (e.g. the post-registration WELCOME_BONUS_PROMO drawer) disables
    // pointer events outside itself, so a toast under it can't be tapped. The
    // headless manager (EnablePushNotificationsPrompt) additionally yields this
    // toast whenever another dialog opens on top of it.
    {
        id: DialogsEnum.PUSH_PERMISSION,
        layer: DialogLayer.MARKETING,
        category: "marketing",
        mode: "queue",
        requiresAuth: true,
        priority: () => 5,
        canShow: isScreenClear,
    },
];
